import { ReactElement, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import getMenuItems from 'routes/menu-items';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import useTenant from 'hooks/useTenant';
import useLocale from 'hooks/useLocale';
import useConfig from 'hooks/useConfig';
import {
  AppModule,
  LayoutType,
  PermissionError,
  Routes
} from 'utils/constants';
import {
  selectAccountInfo,
  selectRealm,
  selectSelectedEmsMemberAccount
} from 'features/account/accountSelectors';
import {
  accountLogout,
  setRealm,
  setTenant
} from 'features/account/accountSlice';
import {
  selectEaiCodebooks,
  selectEaiServiceLevelSpecification,
  selectPermissionError,
  selectShowMenu,
  selectUserGroups
} from 'features/eai/selectors';
import {
  getEaiCodebooks,
  getEaiServiceLevelSpecifications,
  getUserGroups
} from 'features/eai/thunks';
import { fetchAndSetEmsMemberAccountList } from 'features/eai/thunks/emsMemberAccountThunks';
import { getDispatcherCodebooks } from 'features/dispatcher/thunks/dispatcherThunks';
import { selectDispatcherCodebooks } from 'features/dispatcher/selectors/dispatcherSelectors';
import Header from './Header';
import Sidebar from 'components/layout/MainLayout/Sidebar/SideBar';
import Loader from 'components/loading/Loader';
import Breadcrumbs from 'components/assets/Breadcrumbs';
import SelectEmsMemberAccountDialog from 'components/dialog/SelectEmsMemberAccountDialog';
import { IconChevronRight } from '@tabler/icons';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Toolbar,
  useMediaQuery
} from '@mui/material';
import { selectDispatcherBusinessDay } from 'features/dispatcher/selectors/productionPlanningSelectors';
import AuthGuard from 'utils/guards/AuthGuard';
import { clearDispatcherState } from 'features/dispatcher/dispatcherSlice';
import {
  clearEaiState,
  setPermissionError,
  setShowMenu
} from 'features/eai/eaiSlice';
import NoAccessPermissionsForPage from 'views/pages/maintenance/NoAccessPermissionsForPage';
import NoMenuPermissions from 'views/pages/maintenance/NoMenuPermissions';
import { MarketDayMilestonesTracking } from 'utils/marketDayMilestonesTracking/MarketDayMilestonesTracking';
import { Main } from 'components/layout/MainLayout/MainLayoutStyled';
import SessionTimeoutDialog from '../../../views/pages/maintenance/SessionTimeoutDialog';
import { Config } from '../../../utils/envConfig';
import { sendAuditLog } from '../../../features/app/appThunks';
import { revokeAccessToken } from '../../../features/account/accountThunks';
import { useKeycloak } from '../../../contexts/KeycloakContext';

type MainLayoutProps = {
  module: AppModule;
};

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = ({ module }: MainLayoutProps): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const { tenant, realm } = useTenant();
  const { container, layout, drawerOpen } = useConfig();
  const { keycloak } = useKeycloak();

  useLocale();

  const accountInfo = useAppSelector(selectAccountInfo);
  const showMenu = useAppSelector(selectShowMenu);
  const existingRealm = useAppSelector(selectRealm);
  const eaiCodebooks = useAppSelector(selectEaiCodebooks);
  const dispatcherCodebooks = useAppSelector(selectDispatcherCodebooks);
  const userGroups = useAppSelector(selectUserGroups);
  const selectedBusinessDay = useAppSelector(selectDispatcherBusinessDay);
  const errorPermissions = useAppSelector(selectPermissionError);
  const serviceLevelSpecifications = useAppSelector(
    selectEaiServiceLevelSpecification
  );
  const selectedEmsMemberAccount = useAppSelector(
    selectSelectedEmsMemberAccount
  );

  const [showSideBar, setShowSideBar] = useState(false);
  const [errorShowing, setErrorShowing] = useState(false);
  const [drawerOpenState, setDrawerOpenState] = useState(drawerOpen);
  const [isEmsMemberAccountDialogOpen, setIsEmsMemberAccountDialogOpen] =
    useState<boolean>(false);
  const [loggedWithoutPermissions, setLoggedWithoutPermissions] =
    useState(false);

  useEffect(() => {
    setDrawerOpenState(drawerOpen);
  }, [drawerOpen]);

  useEffect(() => {
    if (!existingRealm) {
      dispatch(setRealm(realm));
      dispatch(setTenant(tenant));
    }
  }, [existingRealm, realm, dispatch, tenant]);

  useEffect(() => {
    if (!selectedEmsMemberAccount && existingRealm && !!accountInfo) {
      dispatch(
        fetchAndSetEmsMemberAccountList(true, undefined, false, () => {
          setIsEmsMemberAccountDialogOpen(true);
        })
      );
    }
  }, [selectedEmsMemberAccount, existingRealm, accountInfo, dispatch]);

  useEffect(() => {
    if (!!selectedEmsMemberAccount && !!accountInfo) {
      if (!userGroups?.length) dispatch(getUserGroups());

      if (module === AppModule.DISPATCHER && !dispatcherCodebooks.length) {
        dispatch(getDispatcherCodebooks());
      }

      if (module === AppModule.EAI) {
        !eaiCodebooks.length && dispatch(getEaiCodebooks());
        !serviceLevelSpecifications.length &&
          dispatch(getEaiServiceLevelSpecifications());
      }
    }
  }, [
    module,
    dispatch,
    selectedEmsMemberAccount,
    selectedBusinessDay,
    accountInfo,
    userGroups?.length,
    dispatcherCodebooks.length,
    eaiCodebooks.length,
    serviceLevelSpecifications.length
  ]);

  useEffect(() => {
    if (!selectedEmsMemberAccount) {
      dispatch(clearDispatcherState());
      dispatch(clearEaiState());
    }
  }, [dispatch, selectedEmsMemberAccount]);

  const handleBackButton = () => {
    dispatch(setPermissionError(PermissionError.NO_ERROR));
    setShowSideBar(true);
    navigate(`${Routes.BASE_PATH}${tenant}`);
  };

  const handleLoginButtonSessionTimeout = () => {
    dispatch(
      sendAuditLog({ message: 'User Logout onAuthLogout' }, () =>
        revokeAccessToken(keycloak?.refreshToken, () => {
          keycloak?.logout({
            redirectUri: Config.clientUrl + '/' + tenant
          });
          dispatch(accountLogout);
        })
      )
    );
  };

  const handleBackButtonNoMenu = () => {
    dispatch(setPermissionError(PermissionError.NO_ERROR));
    dispatch(setShowMenu(false));
    setShowSideBar(false);
    setErrorShowing(false);
    setLoggedWithoutPermissions(true);
  };

  const handleSelectMemberAccount = () => {
    dispatch(setPermissionError(PermissionError.NO_ERROR));
    setLoggedWithoutPermissions(false);
    setIsEmsMemberAccountDialogOpen(false);
  };

  const isLargeHorizontalLayout =
    layout === LayoutType.HORIZONTAL_LAYOUT && !matchDownMd;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/*TODO: if possible, move errorPermissions inside AuthGuard*/}
      {errorPermissions !== PermissionError.NO_ERROR &&
        (errorPermissions === PermissionError.UNAUTHORIZED_ERROR ? (
          <SessionTimeoutDialog
            handleLoginButton={handleLoginButtonSessionTimeout}
          />
        ) : (
          <NoAccessPermissionsForPage
            handleBackButton={handleBackButton}
            pageError={errorPermissions === PermissionError.PAGE_ERROR}
          />
        ))}

      {errorShowing && (
        <NoMenuPermissions handleBackButton={handleBackButtonNoMenu} />
      )}

      <AuthGuard>
        {errorPermissions === PermissionError.NO_ERROR && (
          <>
            <Sidebar
              errorShowing={errorShowing}
              loggedWithoutPermissions={loggedWithoutPermissions}
              showSideBar={showSideBar}
              setShowSideBar={setShowSideBar}
              setErrorShowing={setErrorShowing}
            />

            <AppBar
              enableColorOnDark
              position='fixed'
              color='inherit'
              elevation={0}
              sx={{ bgcolor: theme.palette.background.default }}
            >
              <Toolbar sx={{ p: isLargeHorizontalLayout ? '10px' : '16px' }}>
                <Header />
              </Toolbar>
            </AppBar>

            <Main
              theme={theme}
              open={showMenu ? drawerOpenState : true}
              layout={layout}
            >
              <Container
                maxWidth={container ? 'lg' : false}
                {...(!container && { sx: { px: { xs: 0 } } })}
              >
                <Breadcrumbs
                  separator={IconChevronRight}
                  navigation={getMenuItems({})}
                  icon
                  title
                  rightAlign
                />

                {selectedEmsMemberAccount && <Outlet />}
              </Container>
            </Main>
          </>
        )}

        <MarketDayMilestonesTracking />
      </AuthGuard>

      {!selectedEmsMemberAccount && (
        <SelectEmsMemberAccountDialog
          open={isEmsMemberAccountDialogOpen}
          onSelectMemberAccount={handleSelectMemberAccount}
        />
      )}

      <Loader />
    </Box>
  );
};

export default MainLayout;
